import React, { useEffect, useRef, useState } from 'react';
import { api, money } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT } from '../lang.js';
import Icon from '../Icon.jsx';
import { connectRealtime } from '../realtime.js';
import PageSkeleton from '../Skeleton.jsx';

/**
 * Mutfak ekranı (KDS). Siparişler sütunlara göre akar; kart dokununca bir
 * sonraki duruma geçer. Sipariş olayı gelince ANINDA tazelenir; 10 saniyelik
 * yoklama YEDEK olarak kalır — soket düşerse mutfak ekranı sessizce donmamalı
 * (docs/ARCHITECTURE.md §5: gerçek zamanlı süslemedir).
 */
// Etiketler sözlükten: `kds.col.<durum>` ve `kds.next.<sonraki durum>`.
// `icon` = SONRAKİ adımın simgesi (düğmenin üstündeki yazıyla aynı iş):
// onayla → tik, mutfağa al → aşçı külahı, hazır → zil, servis → çatal.
const COLUMNS = [
  { key: 'PENDING_APPROVAL', next: 'ACCEPTED', icon: 'check' },
  { key: 'ACCEPTED', next: 'IN_KITCHEN', icon: 'chef' },
  { key: 'IN_KITCHEN', next: 'READY', icon: 'bell' },
  { key: 'READY', next: 'SERVED', icon: 'utensils' },
];

export default function Kitchen() {
  const t = useT();
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState('');
  const timer = useRef(null);

  const load = () =>
    api('/api/orders?status=AWAITING_PAYMENT,PENDING_APPROVAL,ACCEPTED,IN_KITCHEN,READY')
      .then(setOrders)
      .catch((e) => setErr(errMessage(t, e)));

  useEffect(() => {
    load();
    timer.current = setInterval(load, 10_000);
    const sock = connectRealtime();
    sock?.on('orders:new', load);
    sock?.on('orders:changed', load);
    return () => {
      clearInterval(timer.current);
      sock?.off('orders:new', load);
      sock?.off('orders:changed', load);
    };
    // eslint-disable-next-line
  }, []);

  /** "Hayır, bu sipariş hazırlanıyor" — istek kapanır, sipariş yaşar. */
  async function reject(id) {
    setErr('');
    try {
      await api(`/api/orders/${id}/cancel-request/reject`, { method: 'POST' });
      load();
    } catch (e) {
      setErr(errMessage(t, e));
    }
  }

  async function move(id, status) {
    setErr('');
    try {
      await api(`/api/orders/${id}/status`, { method: 'POST', body: { status } });
      load();
    } catch (e) {
      setErr(errMessage(t, e));
    }
  }

  if (err && !orders) return <p className="err">{err}</p>;
  if (!orders) return <PageSkeleton shape="board" />;

  const awaiting = orders.filter((o) => o.status === 'AWAITING_PAYMENT');

  return (
    <>
      {awaiting.length > 0 && (
        <div className="card">
          <h2>{t('kds.awaitingPayment', { n: awaiting.length })}</h2>
          <p className="muted" style={{ marginTop: 0 }}>{t('kds.awaitingNote')}</p>
          {awaiting.map((o) => (
            <div key={o.id} className="row" style={{ borderBottom: '1px solid var(--line)', padding: '6px 0' }}>
              <b>#{o.code}</b>
              <span className="muted">{o.table?.name || '—'}</span>
              {o.cancelRequestedAt && !o.cancelRequestDecidedAt && (
                <span className="badge warn">{t('kds.cancelRequested')}</span>
              )}
              <span className="spacer" />
              <span>{money(o.totalMinor, o.currency)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {COLUMNS.map((col) => {
          const list = orders.filter((o) => o.status === col.key);
          return (
            <div className="card" key={col.key}>
              <h2>{t(`kds.col.${col.key}`)} ({list.length})</h2>
              {list.length === 0 && <p className="muted" style={{ margin: 0 }}>—</p>}
              {list.map((o) => (
                <div key={o.id} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                  <div className="row">
                    <b>#{o.code}</b>
                    <span className="muted">{o.table?.name || t('kds.takeaway')}</span>
                    <span className="spacer" />
                    <span className="muted" style={{ fontSize: 12 }}>{waited(o.placedAt, t)}</span>
                  </div>
                  {o.items.filter((i) => i.status === 'ACTIVE').map((i) => (
                    <div key={i.id} style={{ fontSize: 14 }}>
                      <b>{i.qty}×</b> {i.nameSnapshot}
                      {i.options.length > 0 && (
                        <div className="muted" style={{ fontSize: 12, paddingLeft: 16 }}>
                          {i.options.map((x) => x.nameSnapshot).join(', ')}
                        </div>
                      )}
                      {i.note && <div className="muted" style={{ fontSize: 12, paddingLeft: 16 }}>“{i.note}”</div>}
                    </div>
                  ))}
                  {o.note && <p className="muted" style={{ fontSize: 13, margin: '6px 0 0' }}>{t('kds.note', { text: o.note })}</p>}
                  {/* Misafirin iptal isteği. Kararı personel verir: onay =
                      siparişin normal iptal yolu, ret = istek kapanır ama
                      sipariş yaşamaya devam eder (docs/STATE-MACHINES.md §1). */}
                  {o.cancelRequestedAt && !o.cancelRequestDecidedAt && (
                    <div className="cancelreq">
                      <b><Icon n="ban" />{t('kds.cancelRequested')}</b>
                      {o.cancelRequestNote && <p className="muted">“{o.cancelRequestNote}”</p>}
                      <div className="row" style={{ gap: 8, marginTop: 6 }}>
                        <button className="danger" onClick={() => move(o.id, 'CANCELED')}>
                          <Icon n="check" />{t('kds.cancelApprove')}
                        </button>
                        <button onClick={() => reject(o.id)}>
                          <Icon n="x" />{t('kds.cancelReject')}
                        </button>
                      </div>
                    </div>
                  )}
                  <button className="primary" style={{ marginTop: 8, width: '100%' }}
                    onClick={() => move(o.id, col.next)}>
                    <Icon n={col.icon} />{t(`kds.next.${col.next}`)}
                  </button>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {err && <p className="err">{err}</p>}
    </>
  );
}

function waited(placedAt, t) {
  const min = Math.floor((Date.now() - new Date(placedAt)) / 60000);
  return min < 1 ? t('kds.new') : t('kds.minutes', { n: min });
}
